import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import {
  Truck, Headphones, Building2, BookOpen, ChevronRight, ExternalLink,
  Search, GraduationCap, DollarSign, Shield, Users, BarChart3,
  FileText, MapPin, Handshake, Scale, Landmark, TrendingUp, Target,
  Fuel, Package, ClipboardList, HeartHandshake, Briefcase, Lightbulb,
  ArrowLeft
} from 'lucide-react';

const DRIVER_RESOURCES = [
  {
    title: 'Foundations of Being a Professional Driver',
    icon: GraduationCap,
    items: [
      { title: 'FMCSA Driver Resources Hub', desc: 'Official tools, regulatory updates and safety guidance for all commercial drivers.', url: 'https://www.fmcsa.dot.gov/driver-resources', source: 'FMCSA.gov' },
      { title: 'DOT Non-CDL Driver Qualification Requirements', desc: 'Complete checklist of DQF requirements for non-CDL interstate drivers under 49 CFR 391.', url: 'https://www.goodhire.com/resources/articles/dot-non-cdl-driver-qualifications-checklist-requirements/', source: 'GoodHire' },
      { title: 'Young Driver Education Toolkit', desc: 'Free lesson plans, videos, tip sheets and student handouts for safe driving around trucks.', url: 'https://www.fmcsa.dot.gov/youngdriver', source: 'FMCSA.gov' },
      { title: 'Our Roads, Our Safety Resources', desc: 'Downloadable materials promoting safe driving practices around large trucks and buses.', url: 'https://www.fmcsa.dot.gov/ourroads/general-safe-driving-resources', source: 'FMCSA.gov' },
    ]
  },
  {
    title: 'Load Handling & Customer Interaction',
    icon: Package,
    items: [
      { title: 'Freight Handling Best Practices', desc: 'Industry guide to securing loads, weight distribution and damage prevention during transport.', url: 'https://www.fmcsa.dot.gov/safety/passenger-safety/cargo-securement-rules', source: 'FMCSA.gov' },
      { title: 'Customer Service for Drivers', desc: 'Tips on professional communication, on-time delivery and building shipper relationships.', url: 'https://www.atbs.com/knowledge-hub', source: 'ATBS Knowledge Hub' },
    ]
  },
  {
    title: 'Trip Planning & Efficiency',
    icon: MapPin,
    items: [
      { title: 'FMCSA Hours of Service Rules', desc: 'Official HOS regulations including driving limits, rest breaks and sleeper berth provisions.', url: 'https://www.fmcsa.dot.gov/hours-service/eld/driver-hours-service-rules', source: 'FMCSA.gov' },
      { title: 'Fuel Savings & Route Optimization', desc: 'Free guides on fuel efficiency, route planning and reducing deadhead miles.', url: 'https://www.apexcapitalcorp.com/resources-white-papers/', source: 'Apex Capital' },
      { title: 'Trucker Path Navigation & Load Board', desc: 'Free mobile app combining GPS navigation with a load board for finding freight on the go.', url: 'https://truckerpath.com', source: 'Trucker Path' },
    ]
  },
  {
    title: 'Compliance Basics (Logs, Inspections)',
    icon: ClipboardList,
    items: [
      { title: 'ELD Compliance Guide', desc: 'Electronic Logging Device requirements and compliance info for commercial vehicles.', url: 'https://www.fmcsa.dot.gov/hours-service/eld/driver-hours-service-rules', source: 'FMCSA.gov' },
      { title: 'TWIC Card Application Guide', desc: 'Step-by-step process for obtaining your Transportation Worker Identification Credential.', url: 'https://www.tsa.gov/for-industry/twic', source: 'TSA.gov' },
      { title: 'Clearinghouse Learning Center', desc: 'Drug and alcohol clearinghouse roles and responsibilities for drivers and employers.', url: 'https://clearinghouse.fmcsa.dot.gov/learn', source: 'FMCSA.gov' },
      { title: '2025 FMCSA Rule Updates', desc: 'New rules including English proficiency requirements and updated inspection protocols.', url: 'https://routemate.us/blog/new-fmcsa-rules-in-2025-what-every-driver-needs-to-know/', source: 'RouteMate' },
    ]
  },
  {
    title: 'Increasing Income as a Driver',
    icon: DollarSign,
    items: [
      { title: 'TruckSmarter Free Load Board', desc: '100% free load board aggregating loads from multiple brokers with "Book It Now" features.', url: 'https://www.trucksmarter.com', source: 'TruckSmarter' },
      { title: 'Amazon Relay for Box Trucks', desc: 'Book Amazon Middle Mile and Last Mile freight directly. Great for 26ft box trucks with liftgates.', url: 'https://relay.amazon.com', source: 'Amazon Relay' },
      { title: 'ATBS Tax Deduction Worksheet', desc: 'Free tools including Per Diem Tracker, Tax Deadline Calendar and trucker-specific deductions.', url: 'https://www.atbs.com/knowledge-hub', source: 'ATBS' },
      { title: 'GoShare Local Delivery Platform', desc: 'Gig-style platform for local big-and-bulky deliveries (Costco, Home Depot). Stay local, earn more.', url: 'https://www.goshare.co', source: 'GoShare' },
    ]
  }
];

const DISPATCHER_RESOURCES = [
  {
    title: 'How to Source and Negotiate Loads',
    icon: Target,
    items: [
      { title: 'Free Truck Dispatching Full Course', desc: 'Complete dispatching course from scratch covering load booking, operations and business setup.', url: 'https://www.youtube.com/watch?v=bsiPiE-zT5c', source: 'YouTube (Gill22)' },
      { title: 'Load Rate Negotiations Guide', desc: 'When and how to negotiate rates effectively. Timing over tactics — negotiate with leverage.', url: 'https://www.learndispatch.com/load-rate-negotiations-in-truck-dispatching/', source: 'LearnDispatch' },
      { title: '10 Proven Rate Negotiation Strategies', desc: 'Leverage carrier reliability, timing, backhaul planning and equipment type for better rates.', url: 'https://www.extremedispatch.com/10-proven-strategies-dispatchers-use-to-negotiate-better-rates/', source: 'Extreme Dispatch' },
      { title: 'DAT One Load Board', desc: 'Industry-leading load board with equipment filters for straight trucks and average rate analytics.', url: 'https://www.dat.com', source: 'DAT' },
    ]
  },
  {
    title: 'Managing Multiple Drivers Effectively',
    icon: Users,
    items: [
      { title: 'Truck Dispatcher Training Manual', desc: 'Free editable manuals covering dispatching 101, equipment terminology, load planning and driver management.', url: 'https://training.safetyculture.com/training-manuals/truck-dispatcher-training-manual/', source: 'SafetyCulture' },
      { title: 'Freight Dispatching from Home Workshop', desc: 'Beginner-friendly free training on managing drivers, booking loads and handling daily operations.', url: 'https://www.youtube.com/watch?v=6EqeV8oIiRI', source: 'YouTube (Leads2Drivers)' },
      { title: 'AlfaX Logistics Training Library', desc: 'Over 70 free videos covering every aspect of truck dispatching from foundations to advanced topics.', url: 'https://www.alfaxlogistics.com/post/free-truck-dispatcher-training-vs-paid-online-courses', source: 'AlfaX Logistics' },
    ]
  },
  {
    title: 'Broker Communication & Relationships',
    icon: Handshake,
    items: [
      { title: '5 Broker Negotiation Tricks for Dispatchers', desc: 'Controlling conversations, using time leverage for short pickup windows and pairing loads.', url: 'https://www.youtube.com/watch?v=ejiIT4aS4MU', source: 'YouTube' },
      { title: 'Dispatcher Negotiation Skills Guide', desc: 'Research lane rates, set objectives, leverage competition and handle objections professionally.', url: 'https://www.scribd.com/document/821166984/Negotiation-Skills-for-Dispatch', source: 'Scribd (Free Preview)' },
      { title: 'Truckstop Broker Credit Data', desc: 'Verify broker credit-worthiness and days-to-pay before committing to loads.', url: 'https://www.truckstop.com', source: 'Truckstop' },
    ]
  },
  {
    title: 'Rate Strategy & Lane Planning',
    icon: BarChart3,
    items: [
      { title: 'DAT Lane Rate Analytics', desc: 'Deep market data showing average rates for specific lanes so you never underbid on freight.', url: 'https://www.dat.com', source: 'DAT' },
      { title: 'How to Negotiate for Better Rates', desc: 'Practical scripting techniques: start with rounded target rates and adjust through negotiation skills.', url: 'https://www.youtube.com/watch?v=lCwlGO-6wBQ', source: 'YouTube' },
      { title: 'LoadConnect Dispatcher Tips', desc: 'Verify brokers before negotiations, use visual tools for quick profitability decisions under pressure.', url: 'https://loadconnect.io/blog/truck-dispatcher-tips', source: 'LoadConnect' },
    ]
  },
  {
    title: 'Dispatcher Ethics & Expectations',
    icon: Scale,
    items: [
      { title: 'Free Dispatcher Course on Alison', desc: 'Self-paced course covering basics of freight dispatching, career paths and professional standards.', url: 'https://alison.com/careers/distribution/freight-dispatcher', source: 'Alison (Free)' },
      { title: 'FMCSA Safety Regulations for Dispatchers', desc: 'Understanding your responsibilities under federal motor carrier safety regulations.', url: 'https://www.fmcsa.dot.gov/safety', source: 'FMCSA.gov' },
    ]
  }
];

const CARRIER_RESOURCES = [
  {
    title: 'Business Setup & Scaling',
    icon: Building2,
    items: [
      { title: 'SBA Business Plan Guide', desc: 'Free templates and step-by-step guides for writing a trucking company business plan.', url: 'https://www.sba.gov/business-guide/plan-your-business/write-your-business-plan', source: 'SBA.gov' },
      { title: 'SCORE Free Mentoring & Templates', desc: 'Free mentoring, business plan templates, webinars and local support chapters.', url: 'https://www.score.org', source: 'SCORE.org' },
      { title: 'Starting a Trucking Company Guide', desc: 'Comprehensive free guide on registration, DOT numbers, permits, insurance and operations.', url: 'https://www.porterunlimitedllc.com/post/essential-resources-for-aspiring-trucking-entrepreneurs', source: 'Porter Unlimited' },
      { title: 'SBDC Virtual Events & Resources', desc: 'Free infographics, checklists, market research courses from Small Business Development Centers.', url: 'https://americassbdc.org', source: 'SBDC' },
    ]
  },
  {
    title: 'Financial Management & Profitability',
    icon: DollarSign,
    items: [
      { title: 'ATBS Tax & Financial Tools', desc: 'Free Per Diem Tracker, Tax Deadline Calendar and Trucker Tax Deduction Worksheet.', url: 'https://www.atbs.com/knowledge-hub', source: 'ATBS' },
      { title: 'Apex Capital Business Resources', desc: 'Free downloadable PDFs on starting a company, scaling operations and maximizing fuel savings.', url: 'https://www.apexcapitalcorp.com/resources-white-papers/', source: 'Apex Capital' },
      { title: 'US Chamber Small Business Resources', desc: 'Free tools, webinars and guides for managing finances, marketing and growing your business.', url: 'https://www.uschamber.com/co/start/strategy/free-resources-for-small-businesses', source: 'US Chamber' },
    ]
  },
  {
    title: 'Hiring Drivers & Building a Team',
    icon: Users,
    items: [
      { title: 'FMCSA Driver Qualification File Guide', desc: 'Required DQF components for non-CDL interstate drivers under federal regulations.', url: 'https://www.avatarfleet.com/blog/what-are-the-dot-requirements-for-non-cdl-drivers-dqf', source: 'AvatarFleet' },
      { title: 'DOT Non-CDL Requirements Checklist', desc: 'Complete checklist of requirements, record-keeping and compliance for hiring non-CDL drivers.', url: 'https://dotdriverfiles.com/understanding-dot-non-cdl-requirements/', source: 'DOT Driver Files' },
    ]
  },
  {
    title: 'Direct Shipper Strategy',
    icon: Briefcase,
    items: [
      { title: 'Sylectus Expedite Network', desc: 'Premium network built specifically for straight trucks, sprinters and cargo vans with carrier networking.', url: 'https://www.sylectus.com', source: 'Sylectus' },
      { title: 'Amazon Relay Direct Freight', desc: 'Book Amazon freight directly. Consistent work for 26ft box trucks with weekly payments.', url: 'https://relay.amazon.com', source: 'Amazon Relay' },
      { title: 'Top 10 Websites for Owner-Operators', desc: 'Curated list of the most valuable platforms and tools for small trucking businesses.', url: 'https://getscalefunding.com/scale-funding-insights/top-10-websites-for-owner-operators-and-small-trucking-companies/', source: 'Scale Funding' },
    ]
  },
  {
    title: 'Contracts, Compliance & Risk Management',
    icon: Shield,
    items: [
      { title: 'FMCSA Registration & Authority', desc: 'Official guide to USDOT numbers, operating authority and compliance requirements.', url: 'https://www.fmcsa.dot.gov/registration', source: 'FMCSA.gov' },
      { title: 'Free Carrier Compliance Guide', desc: 'Comprehensive overview of DOT/FMCSA rules including non-CDL driver qualification files.', url: 'https://tiproservices.com/free-compliance-guide', source: 'TiPro Services' },
      { title: 'FMCSA National Training Center', desc: 'High-quality free safety training programs for motor carriers from the federal government.', url: 'https://www.fmcsa.dot.gov/national-training-center', source: 'FMCSA.gov' },
    ]
  },
  {
    title: 'Grants, Funding & Business Growth',
    icon: Landmark,
    items: [
      { title: 'Free Grants for Trucking Companies', desc: 'Complete database of federal and state grants for small carriers, including NASE, USDA and SBA programs.', url: 'https://www.getdoublenickel.com/blog/free-grants-to-start-a-trucking-company', source: 'Double Nickel' },
      { title: 'FMCSA Grant Programs', desc: 'Federal grant programs for commercial motor vehicle safety training and operations.', url: 'https://www.fmcsa.dot.gov/grants', source: 'FMCSA.gov' },
      { title: 'USDA Rural Business Grants', desc: '$10K-$500K grants for rural businesses with fewer than 50 employees for equipment and infrastructure.', url: 'https://www.rd.usda.gov/programs-services/business-programs', source: 'USDA.gov' },
      { title: 'Grants.gov Federal Search', desc: 'Search all available federal grants including those for transportation and small businesses.', url: 'https://www.grants.gov', source: 'Grants.gov' },
      { title: 'SBA 8(a) Business Development', desc: 'Government contracting program for socially and economically disadvantaged business owners.', url: 'https://www.sba.gov/federal-contracting/contracting-assistance-programs/8a-business-development-program', source: 'SBA.gov' },
      { title: 'DOT Transportation Grants', desc: 'Federal Department of Transportation grant opportunities for carriers and logistics businesses.', url: 'https://www.transportation.gov/grants', source: 'DOT.gov' },
    ]
  }
];

const HUBS = [
  { id: 'driver', label: 'Driver', fullLabel: 'Driver Education Hub', icon: Truck, color: 'blue', gradient: 'from-blue-600 to-cyan-600', bg: 'bg-blue-50', border: 'border-blue-200', accent: 'text-blue-600', badge: 'bg-blue-100 text-blue-700', data: DRIVER_RESOURCES },
  { id: 'dispatcher', label: 'Dispatcher', fullLabel: 'Dispatcher Education Hub', icon: Headphones, color: 'violet', gradient: 'from-violet-600 to-purple-600', bg: 'bg-violet-50', border: 'border-violet-200', accent: 'text-violet-600', badge: 'bg-violet-100 text-violet-700', data: DISPATCHER_RESOURCES },
  { id: 'carrier', label: 'Carrier Owner', fullLabel: 'Carrier (Owner) Education Hub', icon: Building2, color: 'emerald', gradient: 'from-emerald-600 to-green-600', bg: 'bg-emerald-50', border: 'border-emerald-200', accent: 'text-emerald-600', badge: 'bg-emerald-100 text-emerald-700', data: CARRIER_RESOURCES },
];

const Resources = () => {
  const { user } = useAuth();
  const [activeHub, setActiveHub] = useState('driver');
  const [expandedSection, setExpandedSection] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const hub = HUBS.find(h => h.id === activeHub);

  const filteredSections = searchQuery
    ? hub.data.map(section => ({
        ...section,
        items: section.items.filter(item =>
          item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.desc.toLowerCase().includes(searchQuery.toLowerCase())
        )
      })).filter(s => s.items.length > 0)
    : hub.data;

  const totalResources = hub.data.reduce((sum, s) => sum + s.items.length, 0);

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1">
        {user?.role === 'learner' && (
          <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-6">
            <Link
              to="/profile"
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-orange-50 hover:border-orange-300 hover:text-orange-700 transition-colors shadow-sm"
              data-testid="learner-back-to-home-btn"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Home
            </Link>
          </div>
        )}
        {/* Hero */}
        <div className="relative overflow-hidden bg-slate-900">
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px' }}></div>
          <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 backdrop-blur rounded-full text-sm text-slate-300 mb-4 border border-white/10">
                <BookOpen className="h-4 w-4" />
                Free & Open Resources
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-3" data-testid="resources-title">
                Education Hub
              </h1>
              <p className="text-slate-400 max-w-xl mx-auto mb-8 text-base sm:text-lg">
                Curated free resources from FMCSA, SBA, DOT and industry experts — organized by role.
              </p>

              {/* Hub Selector */}
              <div className="flex flex-wrap justify-center gap-2 mb-6">
                {HUBS.map(h => {
                  const Icon = h.icon;
                  const isActive = activeHub === h.id;
                  return (
                    <button
                      key={h.id}
                      onClick={() => { setActiveHub(h.id); setExpandedSection(null); setSearchQuery(''); }}
                      className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                        isActive
                          ? 'bg-white text-slate-900 shadow-lg shadow-white/10'
                          : 'bg-white/10 text-slate-300 hover:bg-white/20 border border-white/10'
                      }`}
                      data-testid={`hub-tab-${h.id}`}
                    >
                      <Icon className="h-4 w-4" />
                      {h.label}
                    </button>
                  );
                })}
              </div>

              {/* Search */}
              <div className="max-w-lg mx-auto relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={`Search ${hub.label.toLowerCase()} resources...`}
                  className="w-full pl-12 pr-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  data-testid="resources-search-input"
                />
              </div>
            </motion.div>
          </div>
        </div>

        {/* Hub Header */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-8 pb-4">
          <motion.div
            key={activeHub}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${hub.gradient} flex items-center justify-center shadow-sm`}>
                <hub.icon className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900" data-testid="active-hub-title">{hub.fullLabel}</h2>
                <p className="text-sm text-slate-500">{totalResources} resources across {hub.data.length} topics</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Resource Sections */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-12">
          <div className="space-y-3">
            <AnimatePresence mode="wait">
              {filteredSections.map((section, idx) => {
                const SectionIcon = section.icon;
                const isOpen = expandedSection === idx;

                return (
                  <motion.div
                    key={`${activeHub}-${idx}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.04 }}
                    className={`border rounded-xl overflow-hidden transition-all ${isOpen ? hub.border + ' shadow-md' : 'border-slate-200 hover:border-slate-300'}`}
                    data-testid={`resource-section-${idx}`}
                  >
                    <button
                      onClick={() => setExpandedSection(isOpen ? null : idx)}
                      className={`w-full flex items-center gap-4 p-5 text-left transition-colors ${isOpen ? hub.bg : 'bg-white hover:bg-slate-50'}`}
                    >
                      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${hub.gradient} flex items-center justify-center flex-shrink-0 shadow-sm`}>
                        <SectionIcon className="h-5 w-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-slate-900">{section.title}</h3>
                        <p className="text-sm text-slate-500 mt-0.5">{section.items.length} resource{section.items.length !== 1 ? 's' : ''}</p>
                      </div>
                      <ChevronRight className={`h-5 w-5 text-slate-400 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`} />
                    </button>

                    <AnimatePresence>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className="px-5 pb-5 pt-1 space-y-2">
                            {section.items.map((item) => (
                              <a
                                key={item.url}
                                href={item.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-start gap-3 p-3.5 rounded-lg bg-white border border-slate-100 hover:border-slate-300 hover:shadow-sm transition-all group"
                                data-testid={`resource-link-${item.title.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase().slice(0, 30)}`}
                              >
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className={`font-semibold text-sm text-slate-900 group-hover:${hub.accent}`}>{item.title}</span>
                                    <ExternalLink className="h-3 w-3 text-slate-300 group-hover:text-slate-500 flex-shrink-0" />
                                  </div>
                                  <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
                                </div>
                                <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full flex-shrink-0 mt-0.5 ${hub.badge}`}>
                                  {item.source}
                                </span>
                              </a>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>

          {searchQuery && filteredSections.length === 0 && (
            <div className="text-center py-16">
              <Search className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No resources match "{searchQuery}"</p>
              <p className="text-slate-400 text-sm mt-1">Try a different search term</p>
              <button onClick={() => setSearchQuery('')} className="mt-3 text-sm text-blue-600 hover:text-blue-700 font-medium">Clear search</button>
            </div>
          )}

          {/* Disclaimer */}
          <div className="mt-10 p-5 bg-white border border-slate-200 rounded-xl text-center">
            <p className="text-sm text-slate-500">
              All resources link to free, publicly available content from government agencies (FMCSA, SBA, DOT, USDA) and reputable industry sources. StraightConnect does not own or host this third-party content.
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Resources;
